import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { areaNum, BASKI_STAGES, BASKI_STAGE_COLORS, stageBadgeCls } from "@/components/shared";
export { BASKI_STAGES, BASKI_STAGE_COLORS, stageBadgeCls };
import { progressPct, PROGRESS_ITEMS } from "@/pages/AssignerPage";
import { Ruler, Boxes, FileText, Printer, Box, X, CheckCircle2, Circle, CalendarClock, AlertTriangle, PlayCircle, Warehouse, ArrowDownUp, Paperclip, ExternalLink, Hammer, Search } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const fmt = (n, d = 1) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: d });

const FILE_META = {
  files: { label: "Tasarım Dosyası Takip", short: "Tasarım", icon: FileText, accent: "bg-violet-500/10 text-violet-600" },
  uretim_files: { label: "Üretim Dosyası Takip", short: "Üretim", icon: Box, accent: "bg-amber-500/10 text-amber-600" },
  baski_files: { label: "Baskı Dosyası Takip", short: "Baskı", icon: Printer, accent: "bg-sky-500/10 text-sky-600" },
};

const SORTS = [
  ["missing", "Yüklenmemiş önce"],
  ["uploaded", "Yüklü önce"],
  ["name", "Firma adı (A-Z)"],
  ["m2", "m² (büyükten küçüğe)"],
  ["progress", "İlerleme (yüksekten düşüğe)"],
];

const FileTrackView = ({ rows, fileKey, onClose, onSelect, testidPrefix }) => {
  const meta = FILE_META[fileKey];
  const Icon = meta.icon;
  const [sort, setSort] = useState("missing");
  const sorted = useMemo(() => {
    const cnt = (p) => (p[fileKey] || []).length;
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "tr");
    const arr = [...rows];
    if (sort === "missing") arr.sort((a, b) => cnt(a) - cnt(b) || byName(a, b));
    else if (sort === "uploaded") arr.sort((a, b) => cnt(b) - cnt(a) || byName(a, b));
    else if (sort === "name") arr.sort(byName);
    else if (sort === "m2") arr.sort((a, b) => areaNum(b.area_m2) - areaNum(a.area_m2) || byName(a, b));
    else if (sort === "progress") arr.sort((a, b) => progressPct(b) - progressPct(a) || byName(a, b));
    return arr;
  }, [rows, sort, fileKey]);
  const done = rows.filter((p) => (p[fileKey] || []).length > 0).length;
  return (
    <div className="fixed inset-0 z-50 bg-[#F5F7FA] overflow-y-auto" data-testid={`${testidPrefix}-filetrack-view`}>
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center ${meta.accent}`}><Icon size={18} /></div>
            <div>
              <div className="font-heading text-lg font-extrabold leading-tight">{meta.label}</div>
              <div className="text-xs text-slate-400">{rows.length} proje · <span className="text-emerald-600 font-semibold">{done} yüklü</span> · <span className={rows.length - done > 0 ? "text-red-500 font-semibold" : ""}>{rows.length - done} yüklenmemiş</span></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <ArrowDownUp size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid={`${testidPrefix}-filetrack-sort`} className="h-9 border border-slate-200 bg-white pl-8 pr-3 text-xs font-semibold">
                {SORTS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
              </select>
            </div>
            <button type="button" onClick={onClose} data-testid={`${testidPrefix}-filetrack-close`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150">
              <X size={14} /> Kapat
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sorted.map((p) => {
          const n = (p[fileKey] || []).length;
          const pct = progressPct(p);
          return (
            <button type="button" key={p.id} onClick={() => onSelect(p)} data-testid={`${testidPrefix}-filetrack-card-${p.id}`} className={`bg-white border p-4 text-left transition-colors duration-150 ${n > 0 ? "border-emerald-200 hover:border-emerald-500" : "border-red-200 hover:border-red-500"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-heading text-sm font-extrabold leading-tight">{p.name}</div>
                <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 border ${n > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`}>{n > 0 ? `${n} dosya` : "Yok"}</span>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">{p.stand_no || "—"} · {p.zone}{p.hub ? ` · ${p.hub}` : ""} · {p.area_m2 || "—"} m²</div>
              <div className="mt-2.5 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 overflow-hidden">
                  <div className={`h-full ${pct === 100 ? "bg-emerald-500" : pct > 0 ? "bg-blue-500" : ""}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[10px] font-bold tabular-nums text-slate-500">%{pct}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const KURULUM_PALETTE = [
  { bar: "bg-sky-400", badge: "bg-sky-50 text-sky-700 border-sky-200" },
  { bar: "bg-blue-400", badge: "bg-blue-50 text-blue-700 border-blue-200" },
  { bar: "bg-indigo-400", badge: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { bar: "bg-violet-400", badge: "bg-violet-50 text-violet-700 border-violet-200" },
  { bar: "bg-purple-400", badge: "bg-purple-50 text-purple-700 border-purple-200" },
  { bar: "bg-fuchsia-400", badge: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  { bar: "bg-pink-400", badge: "bg-pink-50 text-pink-700 border-pink-200" },
  { bar: "bg-rose-400", badge: "bg-rose-50 text-rose-700 border-rose-200" },
  { bar: "bg-orange-400", badge: "bg-orange-50 text-orange-700 border-orange-200" },
  { bar: "bg-amber-400", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  { bar: "bg-lime-500", badge: "bg-lime-50 text-lime-700 border-lime-200" },
  { bar: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];
const kurulumStage = (p) => {
  const done = p.kurulum_progress || [];
  for (let i = PROGRESS_ITEMS.length - 1; i >= 0; i--) if (done.includes(PROGRESS_ITEMS[i])) return PROGRESS_ITEMS[i];
  return null;
};
const kurulumBadgeCls = (stage) => {
  const i = PROGRESS_ITEMS.indexOf(stage);
  return i >= 0 ? KURULUM_PALETTE[i % KURULUM_PALETTE.length].badge : "bg-slate-50 text-slate-500 border-slate-200";
};
const kurulumDaysLeft = (p) => {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec((p.kurulum_tarihi || "").trim());
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  if (isNaN(d)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};

const TaseronInfoBlock = ({ projectId, testidPrefix }) => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get(`/taseron-info/${projectId}`).then((r) => setInfo(r.data)).catch(() => setInfo({}));
  }, [projectId]);
  if (!info || (!info.baski_sureci && !(info.photos || []).length && !(info.teklif_files || []).length)) return null;
  return (
    <div className="border border-sky-200 bg-sky-50/40 p-3 space-y-3" data-testid={`${testidPrefix}-taseron-info`}>
      <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold flex items-center gap-1"><Printer size={11} /> Baskı Taşeronu</div>
      {info.baski_sureci && (
        <div className="text-xs">
          <span className={`inline-block font-bold border px-2 py-1 ${stageBadgeCls(info.baski_sureci)}`}>{info.baski_sureci}</span>
          {info.baski_sureci_by && <span className="text-slate-400 ml-2">{info.baski_sureci_by}{info.baski_sureci_at ? ` · ${new Date(info.baski_sureci_at).toLocaleDateString("tr-TR")}` : ""}</span>}
        </div>
      )}
      {(info.photos || []).length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {info.photos.map((f) => (
            <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer" title={`${f.original_filename} — ${f.uploader_name}`}>
              <img src={`${f.url}?thumb=1`} alt={f.original_filename} loading="lazy" className="w-16 h-16 object-cover border border-slate-200 hover:opacity-90 transition-opacity" />
            </a>
          ))}
        </div>
      )}
      {(info.teklif_files || []).length > 0 && (
        <ul className="space-y-1">
          {info.teklif_files.map((f) => (
            <li key={f.id} className="flex items-center gap-1.5 text-xs">
              <Paperclip size={11} className="text-amber-500 shrink-0" />
              <a href={f.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700 hover:underline truncate">{f.original_filename}</a>
              <span className="text-slate-400 shrink-0">Teklif · {f.uploader_name}</span>
              <ExternalLink size={10} className="text-slate-400 shrink-0" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const SetupDashboard = ({ rows: rowsProp, testidPrefix = "member", editPath = null }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(rowsProp);
  useEffect(() => { setRows(rowsProp); }, [rowsProp]);
  const [showProjects, setShowProjects] = useState(false);
  const [showStarted, setShowStarted] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [detail, setDetail] = useState(null);
  const [hubSel, setHubSel] = useState("all");
  const [cmSel, setCmSel] = useState("all");
  const [query, setQuery] = useState("");
  const [fileView, setFileView] = useState(null);
  const [stageSel, setStageSel] = useState(null);
  const [kurulumSel, setKurulumSel] = useState(null);
  const [warnOpen, setWarnOpen] = useState(false);

  const hubs = useMemo(() => [...new Set(rows.map((p) => p.hub).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [rows]);
  const cms = useMemo(() => [...new Set(rows.map((p) => p.cm_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [rows]);
  const frows = useMemo(() => {
    const q = query.toLocaleLowerCase("tr").trim();
    return rows.filter((p) => {
      if (hubSel !== "all" && p.hub !== hubSel) return false;
      if (cmSel !== "all" && p.cm_name !== cmSel) return false;
      if (!q) return true;
      return [p.name, p.stand_no, p.zone, p.hub, p.cm_name, p.sem_name, p.producer_name, p.project_status]
        .filter(Boolean).join(" ").toLocaleLowerCase("tr").includes(q);
    });
  }, [rows, hubSel, cmSel, query]);

  const total = frows.length;
  const avgProgress = total ? Math.round((frows.reduce((s, p) => s + progressPct(p), 0) / total) * 10) / 10 : 0;
  const ozelCount = frows.filter((p) => p.project_status === "Özel").length;
  const sbbCount = frows.filter((p) => p.project_status === "SBB").length;
  const totalM2 = frows.reduce((s, p) => s + areaNum(p.area_m2), 0);
  const started = frows.filter((p) => progressPct(p) > 0);
  const startedM2 = started.reduce((s, p) => s + areaNum(p.area_m2), 0);

  const sortedRows = useMemo(() => [...frows].sort((a, b) => progressPct(b) - progressPct(a) || (a.name || "").localeCompare(b.name || "", "tr")), [frows]);

  const hubStats = useMemo(() => hubs.map((h) => {
    const hp = rows.filter((p) => p.hub === h);
    const avg = hp.length ? Math.round((hp.reduce((s, p) => s + progressPct(p), 0) / hp.length) * 10) / 10 : 0;
    return { hub: h, count: hp.length, avg, started: hp.filter((p) => progressPct(p) > 0).length, m2: hp.reduce((s, p) => s + areaNum(p.area_m2), 0) };
  }), [hubs, rows]);

  const fileCards = Object.entries(FILE_META).map(([key, m]) => ({ ...m, key, testid: `${testidPrefix}-dash-${key === "files" ? "tasarim" : key === "uretim_files" ? "uretim" : "baski"}` }));

  const chartData = useMemo(() => sortedRows
    .filter((p) => progressPct(p) > 0)
    .map((p) => ({ name: p.name, stand: p.stand_no || "", pct: progressPct(p) })), [sortedRows]);

  const atRisk = useMemo(() => frows
    .filter((p) => progressPct(p) === 0)
    .map((p) => ({ p, days: kurulumDaysLeft(p) }))
    .filter((x) => x.days !== null && x.days <= 7)
    .sort((a, b) => a.days - b.days), [frows]);

  const toggleKurulum = async (step) => {
    const cur = detail.kurulum_progress || [];
    const next = cur.includes(step) ? cur.filter((x) => x !== step) : [...cur, step];
    try {
      await api.patch(`${editPath}/${detail.id}`, { kurulum_progress: next });
      const upd = { ...detail, kurulum_progress: next };
      setDetail(upd);
      setRows((rs) => rs.map((r) => (r.id === upd.id ? upd : r)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap" data-testid={`${testidPrefix}-filter-bar`}>
        <div className="relative flex-1 min-w-[220px] max-w-[360px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ara: müşteri, stand no, hub, CM, üretici..."
            data-testid={`${testidPrefix}-search-input`}
            className="w-full h-9 border border-slate-200 bg-white pl-8 pr-8 text-xs font-semibold focus:outline-none focus:border-slate-900"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} data-testid={`${testidPrefix}-search-clear`} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900"><X size={13} /></button>
          )}
        </div>
        {hubs.length > 0 && (
          <div className="relative">
            <Warehouse size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select value={hubSel} onChange={(e) => setHubSel(e.target.value)} data-testid={`${testidPrefix}-hub-filter`} className={`h-9 border bg-white pl-8 pr-3 text-xs font-bold ${hubSel !== "all" ? "border-emerald-500 text-emerald-700" : "border-slate-200 text-slate-600"}`}>
              <option value="all">Tüm Hublar ({rows.length})</option>
              {hubs.map((h) => <option key={h} value={h}>{h} ({rows.filter((p) => p.hub === h).length})</option>)}
            </select>
          </div>
        )}
        {cms.length > 0 && (
          <div className="relative">
            <select value={cmSel} onChange={(e) => setCmSel(e.target.value)} data-testid={`${testidPrefix}-cm-filter`} className={`h-9 border bg-white px-3 text-xs font-bold ${cmSel !== "all" ? "border-sky-500 text-sky-700" : "border-slate-200 text-slate-600"}`}>
              <option value="all">Tüm CM'ler ({rows.length})</option>
              {cms.map((c) => <option key={c} value={c}>{c} ({rows.filter((p) => p.cm_name === c).length})</option>)}
            </select>
          </div>
        )}
        {(hubSel !== "all" || cmSel !== "all" || query) && (
          <span className="text-[11px] font-bold text-slate-500 tabular-nums" data-testid={`${testidPrefix}-filter-count`}>{frows.length} proje</span>
        )}
        <button type="button" onClick={() => setShowVideo(true)} data-testid={`${testidPrefix}-intro-video-btn`} className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold text-indigo-700 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-colors duration-150">
          <PlayCircle size={14} /> Tanıtım Videosu
        </button>
        {editPath && (
          <button type="button" data-testid={`${testidPrefix}-tv-mode-btn`} className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold text-white bg-slate-900 hover:bg-slate-700 transition-colors duration-150"
            onClick={async () => {
              try {
                window.open(`${window.location.origin}/setupdashboard`, "_blank");
              } catch (e) { toast.error(apiError(e)); }
            }}>
            <ExternalLink size={13} /> TV Modu
          </button>
        )}
      </div>
      {showVideo && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid={`${testidPrefix}-intro-video-modal`} onClick={() => setShowVideo(false)}>
          <div className="bg-black w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900">
              <span className="text-xs font-bold text-white uppercase tracking-wider">Setup Dashboard Tanıtımı</span>
              <button type="button" onClick={() => setShowVideo(false)} data-testid={`${testidPrefix}-intro-video-close`} className="text-slate-400 hover:text-white"><X size={16} /></button>
            </div>
            <video src="/setup-dashboard-tanitim.mp4" controls autoPlay className="w-full max-h-[78vh]" data-testid={`${testidPrefix}-intro-video`} />
          </div>
        </div>
      )}
      {hubSel !== "all" && hubStats.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`${testidPrefix}-hub-grid`}>
          {hubStats.map((h) => (
            <button key={h.hub} type="button" onClick={() => setHubSel(h.hub)} data-testid={`${testidPrefix}-hub-card-${h.hub.replace(/\s+/g, "-").toLowerCase()}`} className={`bg-white border p-4 text-left transition-colors duration-150 ${hubSel === h.hub ? "border-emerald-600" : "border-slate-200 hover:border-slate-400"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-heading text-sm font-extrabold">{h.hub}</div>
                <span className={`font-heading text-base font-extrabold tabular-nums ${h.avg === 100 ? "text-emerald-600" : "text-slate-900"}`}>%{h.avg.toLocaleString("tr-TR")}</span>
              </div>
              <div className="mt-2 h-2 bg-slate-100 overflow-hidden">
                <div className={`h-full ${h.avg === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${h.avg}%` }} />
              </div>
              <div className="mt-2 text-[11px] text-slate-500 tabular-nums">{h.count} proje · {h.started} kurulum başladı · {fmt(h.m2, 1)} m²</div>
            </button>
          ))}
        </div>
      )}
      <div className="bg-white border border-slate-200 p-6" data-testid={`${testidPrefix}-dash-progress`}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Genel Kurulum İlerlemesi{hubSel !== "all" ? ` — ${hubSel}` : ""}</span>
          <span className={`font-heading text-2xl font-extrabold tabular-nums ${avgProgress === 100 ? "text-emerald-600" : "text-slate-900"}`} data-testid={`${testidPrefix}-dash-progress-pct`}>%{avgProgress.toLocaleString("tr-TR")}</span>
        </div>
        <div className="mt-3 h-3 bg-slate-100 overflow-hidden">
          <div className={`h-full transition-all duration-500 ${avgProgress === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${avgProgress}%` }} />
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Kapsamdaki tüm firmaların kurulum ilerlemesinin ortalaması (0-%100)</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <button type="button" onClick={() => { setShowStarted(false); setShowProjects((s) => !s); }} data-testid={`${testidPrefix}-dash-count`} className={`bg-white border p-5 text-left transition-colors duration-150 ${showProjects ? "border-slate-900" : "border-slate-200 hover:border-slate-400"}`} title="Projeleri kart görünümünde aç/kapat">
          <div className="w-10 h-10 flex items-center justify-center bg-blue-500/10 text-blue-600"><Boxes size={18} /></div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-4">Proje Sayısı</div>
          <div className="font-heading text-3xl font-extrabold tabular-nums mt-1">{total}</div>
          <div className="mt-2 text-xs text-slate-500 tabular-nums">Özel: {ozelCount} · SBB: {sbbCount}</div>
          <div className="mt-2 text-[11px] font-semibold text-blue-600">{showProjects ? "Kartları gizle" : "Proje kartlarını göster →"}</div>
        </button>
        <div className="bg-white border border-slate-200 p-5" data-testid={`${testidPrefix}-dash-m2`}>
          <div className="w-10 h-10 flex items-center justify-center bg-emerald-500/10 text-emerald-600"><Ruler size={18} /></div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-4">Toplam m²</div>
          <div className="font-heading text-3xl font-extrabold tabular-nums mt-1">{fmt(totalM2, 1)}</div>
        </div>
        <button type="button" onClick={() => { setShowProjects(false); setShowStarted((s) => !s); }} data-testid={`${testidPrefix}-dash-started-count`} className={`bg-white border p-5 text-left transition-colors duration-150 ${showStarted ? "border-slate-900" : "border-slate-200 hover:border-slate-400"}`} title="Kurulumu başlayan firmaları aç/kapat">
          <div className="w-10 h-10 flex items-center justify-center bg-indigo-500/10 text-indigo-600"><PlayCircle size={18} /></div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-4">Kurulum Başlayan Firma</div>
          <div className="font-heading text-3xl font-extrabold tabular-nums mt-1">{started.length}</div>
          <div className="mt-2 text-xs text-slate-500 tabular-nums">{total ? Math.round((started.length / total) * 100) : 0}% · {total - started.length} başlamadı</div>
          <div className="mt-2 text-[11px] font-semibold text-blue-600">{showStarted ? "Kartları gizle" : "Firmaları göster →"}</div>
        </button>
        <div className="bg-white border border-slate-200 p-5" data-testid={`${testidPrefix}-dash-started-m2`}>
          <div className="w-10 h-10 flex items-center justify-center bg-teal-500/10 text-teal-600"><Ruler size={18} /></div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-4">Kurulum Başlayan m²</div>
          <div className="font-heading text-3xl font-extrabold tabular-nums mt-1">{fmt(startedM2, 1)}</div>
          <div className="mt-2 text-xs text-slate-500 tabular-nums">{totalM2 ? Math.round((startedM2 / totalM2) * 100) : 0}% / toplam {fmt(totalM2, 1)} m²</div>
        </div>
      </div>
      {(showProjects || showStarted) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testidPrefix}-${showStarted ? "started-grid" : "project-grid"}`}>
          {(showStarted ? [...started].sort((a, b) => progressPct(b) - progressPct(a)) : sortedRows).map((p) => {
            const pct = progressPct(p);
            return (
              <button type="button" key={p.id} onClick={() => setDetail(p)} data-testid={`${testidPrefix}-project-card-${p.id}`} className="bg-white border border-slate-200 hover:border-slate-900 p-5 text-left transition-colors duration-150">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-heading text-base font-extrabold leading-tight">{p.name}</div>
                  <div className="flex items-center gap-1 shrink-0">
                    {pct > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5"><PlayCircle size={10} /> Başladı</span>}
                    {p.open_issues > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5"><AlertTriangle size={10} /> {p.open_issues}</span>}
                  </div>
                </div>
                <div className="text-xs text-slate-400 mt-1">{p.stand_no || "—"} · {p.zone}{p.hub ? ` · ${p.hub}` : ""}{p.project_status ? ` · ${p.project_status}` : ""} · {p.area_m2 || "—"} m²</div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-100 overflow-hidden">
                    <div className={`h-full ${pct === 100 ? "bg-emerald-500" : pct > 0 ? "bg-blue-500" : ""}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className={`text-xs font-bold tabular-nums ${pct === 100 ? "text-emerald-600" : "text-slate-700"}`}>%{pct}</span>
                </div>
                <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-400">
                  <span className="inline-flex items-center gap-1"><CalendarClock size={10} /> {p.kurulum_tarihi || "Tarih yok"}</span>
                  <span className={(p.files || []).length ? "text-emerald-600 font-semibold" : ""}>Tasarım {(p.files || []).length}</span>
                  <span className={(p.uretim_files || []).length ? "text-emerald-600 font-semibold" : ""}>Üretim {(p.uretim_files || []).length}</span>
                  <span className={(p.baski_files || []).length ? "text-emerald-600 font-semibold" : ""}>Baskı {(p.baski_files || []).length}</span>
                </div>
                <div className="mt-2 text-[11px] font-semibold text-blue-600">Kurulum raporunu aç →</div>
              </button>
            );
          })}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {fileCards.map((c) => {
          const done = frows.filter((p) => (p[c.key] || []).length > 0).length;
          const Icon = c.icon;
          return (
            <button type="button" key={c.key} onClick={() => setFileView(c.key)} data-testid={c.testid} className="bg-white border border-slate-200 hover:border-slate-900 p-5 text-left transition-colors duration-150" title="Dosya takip detayını aç">
              <div className={`w-10 h-10 flex items-center justify-center ${c.accent}`}><Icon size={18} /></div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-4">{c.label}</div>
              <div className="font-heading text-2xl font-extrabold tabular-nums mt-1">{done} <span className="text-sm font-semibold text-slate-400">yüklü</span></div>
              <div className="mt-1 text-xs tabular-nums"><span className={total - done > 0 ? "text-red-500 font-semibold" : "text-slate-400"}>{total - done} yüklenmemiş</span></div>
              <div className="mt-2 h-1.5 bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }} /></div>
              <div className="mt-2 text-[11px] font-semibold text-blue-600">Detayı aç →</div>
            </button>
          );
        })}
      </div>
      <div className="bg-white border border-slate-200 p-6" data-testid={`${testidPrefix}-kurulum-report`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold inline-flex items-center gap-1.5"><Hammer size={12} className="text-indigo-600" /> Kurulum Süreç Raporu</span>
          <span className="text-[10px] text-slate-400">{frows.filter((p) => (p.kurulum_progress || []).length > 0).length}/{total} projede kurulum başladı · sayılar = adımı işaretlenen projeler</span>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
          {(() => {
            const startedN = frows.filter((p) => (p.kurulum_progress || []).length > 0).length;
            const cnt = (s, i) => (i === 0 ? startedN : frows.filter((p) => (p.kurulum_progress || []).includes(s)).length);
            return [["none", "Kurulum başlamadı", total - startedN, "bg-slate-300"], ...PROGRESS_ITEMS.map((s, i) => [s, s, cnt(s, i), KURULUM_PALETTE[i % KURULUM_PALETTE.length].bar])];
          })().map(([key, lbl, n, bar], idx) => (
            <button type="button" key={key} onClick={() => setKurulumSel((cur) => (cur === key ? null : key))} className={`border p-3 text-left transition-colors duration-150 ${idx === 2 ? "lg:col-start-1" : ""} ${kurulumSel === key ? "border-slate-900 bg-slate-50" : "border-slate-100 hover:border-slate-300"}`} data-testid={`${testidPrefix}-kurulum-stage-${lbl.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="font-heading text-xl font-extrabold tabular-nums">{n}<span className="text-xs font-semibold text-slate-400">/{total}</span></span>
                <span className="text-[10px] font-bold text-slate-400 tabular-nums">%{total ? Math.round((n / total) * 100) : 0}</span>
              </div>
              <div className="text-[10px] text-slate-500 font-semibold leading-tight mt-0.5 flex items-center gap-1.5"><span className={`inline-block w-2 h-2 shrink-0 ${bar}`} />{lbl}</div>
              <div className="mt-2 h-1.5 bg-slate-100 overflow-hidden">
                <div className={`h-full ${bar}`} style={{ width: `${total ? Math.round((n / total) * 100) : 0}%` }} />
              </div>
              <div className={`mt-1.5 text-[10px] font-semibold ${kurulumSel === key ? "text-slate-900" : "text-blue-600"}`}>{kurulumSel === key ? "Listeyi kapat" : "Projeleri gör →"}</div>
            </button>
          ))}
        </div>
        {kurulumSel && (() => {
          const list = frows
            .filter((p) => {
              const done = p.kurulum_progress || [];
              if (kurulumSel === "none") return done.length === 0;
              if (kurulumSel === PROGRESS_ITEMS[0]) return done.length > 0;
              return done.includes(kurulumSel);
            })
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
          return (
            <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`${testidPrefix}-kurulum-stage-list`}>
              <div className="text-xs font-bold text-slate-600 mb-2">{kurulumSel === "none" ? "Kurulumu başlamayan projeler" : kurulumSel === PROGRESS_ITEMS[0] ? "Kurulumu başlayan projeler" : `"${kurulumSel}" adımı işaretlenen projeler`} ({list.length})</div>
              {list.length === 0 ? (
                <div className="text-xs text-slate-400">Bu aşamada proje yok.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[340px] overflow-y-auto pr-1">
                  {list.map((p) => {
                    const st = kurulumStage(p);
                    return (
                      <button type="button" key={p.id} onClick={() => setDetail(p)} className="border border-slate-100 hover:border-slate-900 p-3 text-left transition-colors duration-150" data-testid={`${testidPrefix}-kurulum-stage-proj-${p.id}`}>
                        <div className="font-heading text-xs font-extrabold leading-tight truncate">{p.name}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{p.stand_no || "—"} · {p.zone}{p.hub ? ` · ${p.hub}` : ""} · {p.area_m2 || "—"} m² · %{progressPct(p)}</div>
                        {st && <span className={`inline-block mt-1.5 text-[9px] font-bold border px-1.5 py-0.5 ${kurulumBadgeCls(st)}`}>{st}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
        {atRisk.length > 0 ? (
          <div className="mt-4 border border-red-200 bg-red-50/60 p-4" data-testid={`${testidPrefix}-kurulum-warning-box`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs font-bold text-red-700 inline-flex items-center gap-1.5" data-testid={`${testidPrefix}-kurulum-warning-title`}><AlertTriangle size={13} /> Geciken Kurulum Uyarısı ({atRisk.length})</div>
                <div className="text-[10px] text-red-600/80 mt-0.5">Kurulum tarihi geçmiş veya 7 gün içinde olan ama henüz hiçbir kurulum adımı işaretlenmemiş projeler.</div>
              </div>
              {warnOpen && (
                <button type="button" onClick={() => setWarnOpen(false)} data-testid={`${testidPrefix}-kurulum-warning-close`} className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 border border-red-300 hover:bg-red-100 px-2.5 py-1.5 transition-colors duration-150">
                  <X size={12} /> Kapat
                </button>
              )}
            </div>
            {warnOpen && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid={`${testidPrefix}-kurulum-warning`}>
                {atRisk.map(({ p, days }) => (
                  <button type="button" key={p.id} onClick={() => setDetail(p)} className="bg-white border border-red-200 hover:border-red-500 p-3 text-left transition-colors duration-150" data-testid={`${testidPrefix}-kurulum-warning-proj-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-heading text-xs font-extrabold leading-tight truncate">{p.name}</div>
                      <span className={`shrink-0 text-[9px] font-bold border px-1.5 py-0.5 ${days < 0 ? "bg-red-600 text-white border-red-600" : days === 0 ? "bg-red-100 text-red-700 border-red-300" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
                        {days < 0 ? `${-days} gün gecikti` : days === 0 ? "Bugün" : `${days} gün kaldı`}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{p.stand_no || "—"} · {p.zone}{p.hub ? ` · ${p.hub}` : ""} · {p.area_m2 || "—"} m²</div>
                    <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1"><CalendarClock size={10} className="text-red-500" /> Kurulum: {p.kurulum_tarihi}</div>
                  </button>
                ))}
              </div>
            )}
            {!warnOpen && (
              <div className="mt-2 flex justify-end">
                <button type="button" onClick={() => setWarnOpen(true)} data-testid={`${testidPrefix}-kurulum-warning-details`} className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 transition-colors duration-150">
                  Detaylar →
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 text-[11px] text-emerald-600 font-semibold inline-flex items-center gap-1.5" data-testid={`${testidPrefix}-kurulum-warning-ok`}><CheckCircle2 size={13} /> Gecikmiş veya tarihi yaklaşan ama başlamamış kurulum yok.</div>
        )}
      </div>
      <div className="bg-white border border-slate-200 p-6" data-testid={`${testidPrefix}-baski-report`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold inline-flex items-center gap-1.5"><Printer size={12} className="text-sky-600" /> Baskı Süreç Raporu</span>
          <span className="text-[10px] text-slate-400">{frows.filter((p) => p.baski_sureci).length}/{total} projede süreç girildi</span>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
          {[["none", "Süreç girilmedi", frows.filter((p) => !p.baski_sureci).length, "bg-slate-300"], ...BASKI_STAGES.map((s) => [s, s, frows.filter((p) => p.baski_sureci === s).length, BASKI_STAGE_COLORS[s].bar])].map(([key, lbl, n, bar]) => (
            <button type="button" key={key} onClick={() => setStageSel((cur) => (cur === key ? null : key))} className={`border p-3 text-left transition-colors duration-150 ${stageSel === key ? "border-slate-900 bg-slate-50" : "border-slate-100 hover:border-slate-300"}`} data-testid={`${testidPrefix}-baski-stage-${lbl.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="font-heading text-xl font-extrabold tabular-nums">{n}</div>
              <div className="text-[10px] text-slate-500 font-semibold leading-tight mt-0.5 flex items-center gap-1.5"><span className={`inline-block w-2 h-2 shrink-0 ${bar}`} />{lbl}</div>
              <div className="mt-2 h-1.5 bg-slate-100 overflow-hidden">
                <div className={`h-full ${bar}`} style={{ width: `${total ? Math.round((n / total) * 100) : 0}%` }} />
              </div>
              <div className={`mt-1.5 text-[10px] font-semibold ${stageSel === key ? "text-slate-900" : "text-blue-600"}`}>{stageSel === key ? "Listeyi kapat" : "Projeleri gör →"}</div>
            </button>
          ))}
        </div>
        {stageSel && (() => {
          const list = frows
            .filter((p) => (stageSel === "none" ? !p.baski_sureci : p.baski_sureci === stageSel))
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
          return (
            <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`${testidPrefix}-baski-stage-list`}>
              <div className="text-xs font-bold text-slate-600 mb-2">{stageSel === "none" ? "Süreç girilmeyen projeler" : `"${stageSel}" aşamasındaki projeler`} ({list.length})</div>
              {list.length === 0 ? (
                <div className="text-xs text-slate-400">Bu aşamada proje yok.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[340px] overflow-y-auto pr-1">
                  {list.map((p) => (
                    <button type="button" key={p.id} onClick={() => setDetail(p)} className="border border-slate-100 hover:border-slate-900 p-3 text-left transition-colors duration-150" data-testid={`${testidPrefix}-baski-stage-proj-${p.id}`}>
                      <div className="font-heading text-xs font-extrabold leading-tight truncate">{p.name}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{p.stand_no || "—"} · {p.zone}{p.hub ? ` · ${p.hub}` : ""} · {p.area_m2 || "—"} m²</div>
                      {p.baski_sureci && <span className={`inline-block mt-1.5 text-[9px] font-bold border px-1.5 py-0.5 ${stageBadgeCls(p.baski_sureci)}`}>{p.baski_sureci}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
      <div className="bg-white border border-slate-200 p-6" data-testid={`${testidPrefix}-dash-chart`}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Firma Bazında Kurulum İlerlemesi (%)</span>
          <span className="text-[10px] text-slate-400">Yalnız kurulumu başlayan firmalar ({chartData.length}/{total})</span>
        </div>
        {chartData.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400">Henüz kurulumu başlayan firma yok.</div>
        ) : (
          <div style={{ width: "100%", height: Math.max(160, chartData.length * 28 + 60) }}>
            <ResponsiveContainer>
              <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 40, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `%${v}`} />
                <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 11, fill: "#475569" }} interval={0} tickFormatter={(v) => (v.length > 24 ? `${v.slice(0, 23)}…` : v)} />
                <Tooltip formatter={(v) => [`%${v}`, "İlerleme"]} labelFormatter={(l, pl) => `${l}${pl?.[0]?.payload?.stand ? ` — ${pl[0].payload.stand}` : ""}`} contentStyle={{ borderRadius: 0, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="pct" barSize={14} radius={0} isAnimationActive={false} label={{ position: "right", fontSize: 10, fill: "#64748b", formatter: (v) => `%${v}` }}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.pct === 100 ? "#10b981" : "#3b82f6"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {fileView && <FileTrackView rows={frows} fileKey={fileView} onClose={() => setFileView(null)} onSelect={(p) => setDetail(p)} testidPrefix={testidPrefix} />}
      {detail && (() => {
        const pct = progressPct(detail);
        const done = detail.kurulum_progress || [];
        return (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid={`${testidPrefix}-project-detail`} onClick={() => setDetail(null)}>
            <div className="bg-white w-full max-w-[560px] max-h-[88vh] overflow-y-auto border border-slate-200" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-lg font-extrabold leading-tight">{detail.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{detail.stand_no || "—"} · {detail.zone}{detail.project_status ? ` · ${detail.project_status}` : ""} · {detail.area_m2 || "—"} m² · Kurulum: {detail.kurulum_tarihi || "tarih girilmemiş"}</div>
                  {(detail.sem_name || detail.cm_name || detail.producer_name) && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testidPrefix}-project-detail-people`}>
                      {detail.sem_name && <span className="text-[10px] font-bold border border-violet-200 bg-violet-50 text-violet-700 px-2 py-0.5">SEM: {detail.sem_name}</span>}
                      {detail.cm_name && <span className="text-[10px] font-bold border border-sky-200 bg-sky-50 text-sky-700 px-2 py-0.5">CM: {detail.cm_name}</span>}
                      {detail.producer_name && <span className="text-[10px] font-bold border border-amber-200 bg-amber-50 text-amber-700 px-2 py-0.5">Üretici: {detail.producer_name}</span>}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => setDetail(null)} data-testid={`${testidPrefix}-project-detail-close`} className="text-slate-400 hover:text-slate-900 p-1"><X size={18} /></button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Kurulum İlerlemesi</span>
                    <span className={`font-heading text-xl font-extrabold tabular-nums ${pct === 100 ? "text-emerald-600" : "text-slate-900"}`}>%{pct}</span>
                  </div>
                  <div className="mt-2 h-2.5 bg-slate-100 overflow-hidden">
                    <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="border border-slate-200 divide-y divide-slate-100">
                  {PROGRESS_ITEMS.map((step, i) => {
                    const ok = done.includes(step);
                    const Row = editPath ? "button" : "div";
                    return (
                      <Row key={step} {...(editPath ? { type: "button", onClick: () => toggleKurulum(step) } : {})} className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left ${ok ? "bg-emerald-50/50" : ""} ${editPath ? "cursor-pointer hover:bg-slate-50 transition-colors duration-100" : ""}`} data-testid={`${testidPrefix}-detail-step-${i}`}>
                        {ok ? <CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> : <Circle size={14} className="text-slate-300 shrink-0" />}
                        <span className={ok ? "text-slate-800 font-semibold" : "text-slate-400"}>{step}</span>
                      </Row>
                    );
                  })}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[["Tasarım", (detail.files || []).length], ["Üretim", (detail.uretim_files || []).length], ["Baskı", (detail.baski_files || []).length]].map(([lbl, n]) => (
                    <div key={lbl} className={`border p-3 text-center ${n > 0 ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`}>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{lbl}</div>
                      <div className={`font-heading text-lg font-extrabold tabular-nums ${n > 0 ? "text-emerald-600" : "text-slate-300"}`}>{n}</div>
                      <div className="text-[10px] text-slate-400">{n > 0 ? "dosya yüklü" : "dosya yok"}</div>
                    </div>
                  ))}
                </div>
                {detail.open_issues > 0 && (
                  <button type="button" onClick={() => navigate(`/saha-fotograflari?project=${detail.id}&sorun=1`)} className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold bg-red-50 text-red-700 border border-red-200 px-3 py-2.5 hover:bg-red-100 transition-colors duration-150" data-testid={`${testidPrefix}-detail-issues-link`}>
                    <AlertTriangle size={13} /> {detail.open_issues} açık sorun — saha fotoğraflarında gör
                  </button>
                )}
                <TaseronInfoBlock projectId={detail.id} testidPrefix={testidPrefix} />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
